import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Chip,
    Divider,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    alpha,
    useTheme,
    Tooltip,
    Skeleton,
} from "@mui/material";
import {
    CreditCard,
    Delete,
    Add,
    Payment,
    Shield,
    Lock,
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import CryptoJS from "crypto-js";
import { KEY } from "../../../Constants";
import { showSnackbar } from "../../../components/SnackBar/SnackBar";
import {
    getSavedCards,
    deleteSavedCard,
    initializeSubscription,
    chargeSavedCard,
} from "../../../services/api/cardPayments";

// Card brand logos/colors
const cardBrandStyles = {
    visa: {
        color: "#1A1F71",
        bgColor: "#F7F9FC",
        label: "VISA",
    },
    mastercard: {
        color: "#EB001B",
        bgColor: "#FFF8F0",
        label: "Mastercard",
    },
    verve: {
        color: "#00425F",
        bgColor: "#F0F8FF",
        label: "Verve",
    },
    generic: {
        color: "#666",
        bgColor: "#F5F5F5",
        label: "Card",
    },
};

export default function PaymentMethods({ snackBar }) {
    const theme = useTheme();

    // State
    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [saveCard, setSaveCard] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [charging, setCharging] = useState(false);

    // Subscription info from localStorage
    const [subscriptionAmount, setSubscriptionAmount] = useState(0);
    const [expiryDate, setExpiryDate] = useState(null);

    // Get subscription info from localStorage (same as Topbar)
    const getSubscriptionInfo = () => {
        try {
            const encryptedSub = localStorage.getItem("subscriptionDetails");
            if (encryptedSub) {
                const decrypted = CryptoJS.AES.decrypt(encryptedSub, KEY).toString(CryptoJS.enc.Utf8);
                if (decrypted) {
                    const parsed = JSON.parse(decrypted);
                    const amount = Math.ceil(parsed?.[1]?.[1] || 0);
                    return amount;
                }
            }
        } catch (e) {
            console.error("Error reading subscription details:", e);
        }
        return 0;
    };

    // Get expiry date from localStorage
    const getExpiryDate = () => {
        try {
            const encryptedData = localStorage.getItem("myMap");
            if (encryptedData) {
                const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8);
                if (decrypted) {
                    const userData = JSON.parse(decrypted);
                    if (Array.isArray(userData)) {
                        for (const [key, value] of userData) {
                            if (key === "subscription_expires_at" && value) {
                                const date = new Date(value);
                                return (
                                    date.getDate() + " " +
                                    date.toLocaleString("en-US", { month: "long" }) + ", " +
                                    date.getFullYear()
                                );
                            }
                        }
                    }
                }
            }
        } catch (e) {
            console.error("Error reading expiry date:", e);
        }
        return null;
    };

    // Fetch saved cards and subscription info on mount
    useEffect(() => {
        setSubscriptionAmount(getSubscriptionInfo());
        setExpiryDate(getExpiryDate());
        fetchCards();
    }, []);

    const fetchCards = async () => {
        setLoading(true);
        try {
            const response = await getSavedCards();
            if (response.ok && response.data?.success) {
                setCards(response.data.cards || []);
            } else {
                setCards([]);
            }
        } catch (error) {
            console.error("Error fetching cards:", error);
            setCards([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle delete card
    const handleDeleteClick = (card) => {
        setCardToDelete(card);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!cardToDelete) return;

        setDeleting(true);
        try {
            const response = await deleteSavedCard(cardToDelete.id);
            if (response.ok && response.data?.success) {
                showSnackbar({
                    title: "Success!",
                    message: "Card deleted successfully",
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" },
                });
                fetchCards();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.message || "Failed to delete card",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error("Error deleting card:", error);
            showSnackbar({
                title: "Error!",
                message: "Failed to delete card. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setCardToDelete(null);
        }
    };

    // Handle new card payment
    const handleAddNewCard = () => {
        setPaymentDialogOpen(true);
    };

    const handlePaymentSubmit = async () => {
        if (!subscriptionAmount || subscriptionAmount <= 0) {
            showSnackbar({
                title: "Error!",
                message: "Invalid subscription amount",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        setProcessing(true);
        try {
            const response = await initializeSubscription({
                amount: subscriptionAmount,
                saveCard: saveCard,
                subscriptionPlan: "STANDARD",
            });

            if (response.ok && response.data?.success) {
                // Open Paystack checkout in a new tab
                window.open(response.data.authorization_url, '_blank');
                setPaymentDialogOpen(false);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.message || "Failed to initialize payment",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error("Error initializing payment:", error);
            showSnackbar({
                title: "Error!",
                message: "Failed to initialize payment. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setProcessing(false);
        }
    };

    // Handle charge saved card
    const handleChargeCard = (card) => {
        setSelectedCard(card);
        setChargeDialogOpen(true);
    };

    const handleChargeConfirm = async () => {
        if (!selectedCard) return;

        if (!subscriptionAmount || subscriptionAmount <= 0) {
            showSnackbar({
                title: "Error!",
                message: "Invalid subscription amount",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        setCharging(true);
        try {
            const response = await chargeSavedCard({
                cardTokenId: selectedCard.id,
                amount: subscriptionAmount,
                description: "Subscription Payment",
            });

            if (response.ok && response.data?.success) {
                showSnackbar({
                    title: "Success!",
                    message: `Payment of KES ${subscriptionAmount.toLocaleString()} successful!`,
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" },
                });
                setChargeDialogOpen(false);
            } else {
                showSnackbar({
                    title: "Payment Failed",
                    message: response.data?.message || "Failed to charge card",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error("Error charging card:", error);
            showSnackbar({
                title: "Error!",
                message: "Failed to process payment. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setCharging(false);
        }
    };

    // Get card brand style
    const getCardStyle = (cardType) => {
        const type = cardType?.toLowerCase();
        return cardBrandStyles[type] || cardBrandStyles.generic;
    };

    // Render card skeleton
    const CardSkeleton = () => (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Skeleton variant="rectangular" width={60} height={40} sx={{ borderRadius: 1 }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                </Box>
                <Skeleton variant="circular" width={40} height={40} />
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                    Payment Methods
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage your saved cards and payment preferences
                </Typography>
            </Box>

            {/* Security Notice */}
            <Alert
                severity="info"
                icon={<Shield fontSize="small" />}
                sx={{ mb: 3, borderRadius: 2 }}
            >
                <Typography variant="body2">
                    Your card information is securely stored by Paystack. We never store your full card details.
                </Typography>
            </Alert>

            {/* Subscription Info */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    mb: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Payment color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        Subscription
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Subscription Amount
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="primary">
                            {subscriptionAmount > 0
                                ? `KES ${subscriptionAmount.toLocaleString()}`
                                : "Loading..."}
                        </Typography>
                        {expiryDate && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Expires: {expiryDate}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<CreditCard />}
                        onClick={handleAddNewCard}
                        disabled={!subscriptionAmount || subscriptionAmount <= 0}
                        sx={{ borderRadius: 2 }}
                    >
                        Pay with New Card
                    </Button>
                </Box>
            </Paper>

            {/* Saved Cards Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CreditCard color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                            Saved Cards
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddNewCard}
                        disabled={!subscriptionAmount || subscriptionAmount <= 0}
                        sx={{ borderRadius: 2 }}
                    >
                        Add Card
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <CardSkeleton />
                        <CardSkeleton />
                    </Box>
                ) : cards.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 6,
                            px: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                            borderRadius: 2,
                            border: "2px dashed",
                            borderColor: "divider",
                        }}
                    >
                        <CreditCard sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Saved Cards
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Add a card to make quick and easy subscription payments
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleAddNewCard}
                            disabled={!subscriptionAmount || subscriptionAmount <= 0}
                            sx={{ borderRadius: 2 }}
                        >
                            Add Your First Card
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {cards.map((card) => {
                            const brandStyle = getCardStyle(card.card_type);
                            return (
                                <Paper
                                    key={card.id}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: "1px solid",
                                        borderColor: card.is_default ? "primary.main" : "divider",
                                        borderRadius: 2,
                                        bgcolor: brandStyle.bgColor,
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            boxShadow: 1,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        {/* Card Brand */}
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 40,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "white",
                                                borderRadius: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                sx={{ color: brandStyle.color }}
                                            >
                                                {brandStyle.label}
                                            </Typography>
                                        </Box>

                                        {/* Card Details */}
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {card.masked_number || `**** **** **** ${card.last4}`}
                                                </Typography>
                                                {card.is_default && (
                                                    <Chip
                                                        label="Default"
                                                        size="small"
                                                        color="primary"
                                                        sx={{ height: 20, fontSize: "0.7rem" }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Expires: {card.expiry || `${card.exp_month}/${card.exp_year}`}
                                                {card.bank && ` | ${card.bank}`}
                                            </Typography>
                                        </Box>

                                        {/* Actions */}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Payment />}
                                                onClick={() => handleChargeCard(card)}
                                                disabled={!subscriptionAmount || subscriptionAmount <= 0}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Pay
                                            </Button>
                                            <Tooltip title="Delete card">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteClick(card)}
                                                    sx={{
                                                        color: "error.main",
                                                        "&:hover": {
                                                            bgcolor: alpha(theme.palette.error.main, 0.1),
                                                        },
                                                    }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Card</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the card ending in{" "}
                        <strong>{cardToDelete?.last4}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        loading={deleting}
                    >
                        Delete
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* New Card Payment Dialog */}
            <Dialog
                open={paymentDialogOpen}
                onClose={() => !processing && setPaymentDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CreditCard color="primary" />
                        Pay Subscription
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        A new tab will open with Paystack's secure checkout to enter your card details.
                    </Alert>

                    {/* Amount display */}
                    <Paper
                        variant="outlined"
                        sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Subscription Amount
                                </Typography>
                                {expiryDate && (
                                    <Typography variant="caption" color="text.secondary">
                                        Expires: {expiryDate}
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="h5" fontWeight={700} color="primary">
                                KES {subscriptionAmount.toLocaleString()}
                            </Typography>
                        </Box>
                    </Paper>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={saveCard}
                                onChange={(e) => setSaveCard(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Save card for future payments</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Your card will be securely stored by Paystack
                                </Typography>
                            </Box>
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setPaymentDialogOpen(false)}
                        disabled={processing}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handlePaymentSubmit}
                        loading={processing}
                        startIcon={<Lock />}
                    >
                        Proceed to Payment
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Charge Saved Card Dialog */}
            <Dialog
                open={chargeDialogOpen}
                onClose={() => !charging && setChargeDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Payment color="primary" />
                        Pay with Saved Card
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedCard && (
                        <Paper
                            variant="outlined"
                            sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: getCardStyle(selectedCard.card_type).bgColor }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 40,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "white",
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ color: getCardStyle(selectedCard.card_type).color }}
                                    >
                                        {getCardStyle(selectedCard.card_type).label}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>
                                        **** **** **** {selectedCard.last4}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedCard.bank}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}

                    <Paper
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body1">Amount to charge:</Typography>
                            <Typography variant="h5" fontWeight={700} color="success.main">
                                KES {subscriptionAmount.toLocaleString()}
                            </Typography>
                        </Box>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setChargeDialogOpen(false)}
                        disabled={charging}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="success"
                        onClick={handleChargeConfirm}
                        loading={charging}
                        startIcon={<Payment />}
                    >
                        Pay KES {subscriptionAmount.toLocaleString()}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
